/* Country login report: logins / failed / blocked per country + blocked attempts list. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { BellRing } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export const CountryReport = () => {
  const [days, setDays] = useState(7);
  const [r, setR] = useState(null);
  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/security/country-report`, { headers: getAuthHeader(), params: { days } });
      setR(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load country report"); }
  }, [days]);
  useEffect(() => { load(); }, [load]);
  const sendNow = async () => {
    try { await axios.post(`${API}/admin/security/country-report/send-now`, {}, { headers: getAuthHeader() }); toast.success("Report sent to admins' bell"); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };
  return (
    <section className="rounded-lg border bg-white p-3 space-y-3" data-testid="country-report">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-slate-800">Country login report</h4>
        <span className="text-[11px] text-slate-500">Sent to admins' bell every Monday 9:00 AM IST</span>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="ml-auto h-7 rounded border border-slate-300 px-1 text-xs" data-testid="country-report-days">
          <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option>
        </select>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={sendNow} data-testid="country-report-send-now">
          <BellRing className="w-3 h-3 mr-1" /> Send to bell now
        </Button>
      </div>
      {r && (
        <div className="grid gap-3 lg:grid-cols-2">
          <table className="w-full text-xs" data-testid="country-report-table">
            <thead className="bg-slate-50 text-slate-600"><tr>
              <th className="p-2 text-left">Country</th><th className="p-2 text-right">Logins</th><th className="p-2 text-right">Users</th>
              <th className="p-2 text-right">Failed</th><th className="p-2 text-right">Blocked</th></tr></thead>
            <tbody>
              {r.countries.map((c, i) => (
                <tr key={c.country_code || c.country} className={`border-t ${c.blocked ? "bg-red-50" : ""}`} data-testid={`country-report-row-${i}`}>
                  <td className="p-2 font-medium text-slate-800">
                    {c.country_code && <img src={`https://flagcdn.com/16x12/${c.country_code}.png`} alt="" width={16} height={12} className="inline mr-1" />}
                    {c.country}
                  </td>
                  <td className="p-2 text-right font-bold text-purple-700">{c.logins}</td>
                  <td className="p-2 text-right">{c.users}</td>
                  <td className="p-2 text-right text-amber-700">{c.failed}</td>
                  <td className="p-2 text-right font-bold text-red-600">{c.blocked}</td>
                </tr>
              ))}
              {!r.countries.length && <tr><td colSpan={5} className="p-4 text-center text-slate-400">No logins in this period</td></tr>}
            </tbody>
          </table>
          <div>
            <p className="text-xs font-semibold text-red-700 mb-1" data-testid="country-report-blocked-count">Blocked attempts: {r.total_blocked}</p>
            <div className="max-h-64 overflow-auto rounded border">
              <table className="w-full text-xs"><tbody>
                {r.blocked.map((b, i) => (
                  <tr key={`${b.at}-${i}`} className="border-t" data-testid={`country-report-blocked-${i}`}>
                    <td className="p-1.5 whitespace-nowrap text-slate-500">{fmt(b.at)}</td>
                    <td className="p-1.5">{b.email}</td>
                    <td className="p-1.5 text-red-700">{b.where || b.ip}</td>
                    <td className="p-1.5 text-slate-400">{b.reason.startsWith("session ended") ? "live session" : "login"}</td>
                  </tr>
                ))}
                {!r.blocked.length && <tr><td className="p-4 text-center text-slate-400">No blocked attempts</td></tr>}
              </tbody></table>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
